/**
 * Комментарий и задача прямо в строке таблицы.
 *
 * Бренды ведут не поштучно: человек идёт по списку сверху вниз и пишет
 * напротив строки короткую заметку — «спросить у поставщика», «ждём макеты».
 * Заставлять его ради одной фразы открывать карточку — значит превращать
 * пять секунд в полминуты и терять взгляд с высоты.
 *
 * В ленте живут две сущности. Заметка просто висит. Задача видна красным,
 * у неё бывает срок, и её закрывают галочкой — с обязательным отчётом
 * «что сделано». Отчёт становится новым последним комментарием бренда:
 * в таблице сразу видно, чем дело кончилось.
 *
 * Если задача открыта, но сверху лёг другой комментарий, в ячейке горит
 * флажок: у бренда есть несделанное.
 */
import { useEffect, useRef, useState } from 'react'
import type { Comment, CommentKind } from '@shared/types.ts'
import {
  Avatar, Checkbox, IconClose, IconSend, IconWarning, Menu, Popover, Spinner, Tooltip,
  type MenuItem,
} from '../../../../components/index.js'
import { api, ApiRequestError } from '../../../../lib/api.js'
import { shortName } from '../../../../lib/names.js'
import { useApp } from '../../../../store/app-store.js'
import { tableTexts as t } from '../../texts.table.js'
import type { CellProps } from './CellBox.js'

/**
 * «12 окт» — без года и без часов: в таблице важно «когда примерно», а не
 * «во сколько». Точное время видно в самом обсуждении.
 */
function shortDate(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const sameYear = date.getFullYear() === new Date().getFullYear()
  return date.toLocaleDateString('ru-RU', {
    day: 'numeric', month: 'short', ...(sameYear ? null : { year: '2-digit' }),
  }).replace('.', '')
}

/** В ленте время уместно: там читают разговор, а не сканируют список. */
function threadDate(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  if (date.toDateString() === new Date().toDateString()) {
    return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
  }
  return shortDate(iso)
}

/** «до 10 апреля» — срок задачи словами, как его и пишут в комментариях. */
function dueLabel(due: string): string {
  const date = new Date(`${due}T12:00:00`)
  if (Number.isNaN(date.getTime())) return due
  return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })
}

export function CommentCell({ row, ctx, actions }: CellProps) {
  const brand = row.kind === 'brand' ? row.brand : null
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const cellRef = useRef<HTMLButtonElement | null>(null)

  if (!brand) return null

  /* Снимок брендов уже содержит черновые записи автора — отдельная ветка
     по наложению черновика была бы вторым источником правды и теряла бы
     и автора, и цитату, и срок. */
  const last = brand.lastComment
  const task = brand.openTask

  const text = last?.body ?? ''
  const isDraft = last?.isDraft === true
  const isTask = last?.kind === 'task' && last.doneAt === null
  /* Задача открыта, но сверху уже лёг другой комментарий — тогда флажок. */
  const flagged = Boolean(task) && !isTask
  const author = last?.author ?? null
  const color = last?.authorColor ?? null
  const when = isDraft
    ? t.comment.now
    : last
      ? (last.dateKnown ? shortDate(last.at) : t.comment.fromSheet)
      : ''

  const classes = ['cell-comment']
  if (text === '') classes.push('cell-comment--empty')
  if (isTask) classes.push('cell-comment--task')
  if (isDraft) classes.push('cell-comment--draft')

  return (
    <>
      <button
        ref={(node) => { cellRef.current = node }}
        type="button"
        className={classes.join(' ')}
        aria-label={text === '' ? t.comment.addFor(brand.name) : t.comment.openFor(brand.name)}
        onMouseDown={(event) => event.stopPropagation()}
        onClick={(event) => {
          event.stopPropagation()
          setAnchor(cellRef.current)
        }}
      >
        {text === '' ? (
          <span className="cell-comment__empty">
            <span aria-hidden="true">⌁</span>
            {t.comment.empty}
          </span>
        ) : (
          <>
            {author ? (
              <Avatar name={author} size={18} color={color ?? undefined} />
            ) : (
              <span className="cell-comment__dot" aria-hidden="true" />
            )}
            <span className="cell-comment__lines">
              {/* Ответ видно сразу: маленькая цитата над текстом. Без неё
                  «тест» в строке читается как самостоятельная мысль, а не
                  как ответ на задачу. */}
              {last?.replyTo ? (
                <span className="cell-comment__quote">
                  <span aria-hidden="true">↳</span>
                  {` ${shortName(last.replyTo.author)} · ${last.replyTo.body}`}
                </span>
              ) : null}
              <span className="cell-comment__text">
                {isTask && last?.dueOn ? (
                  <span className="cell-comment__due">{t.comment.due(dueLabel(last.dueOn))}</span>
                ) : null}
                {text}
              </span>
            </span>
            <span className="cell-comment__meta">
              {flagged ? (
                <Tooltip content={t.comment.openTask(task?.body ?? '')}>
                  <span className="cell-comment__flag" aria-label={t.comment.hasTask}>
                    <IconWarning size={16} />
                  </span>
                </Tooltip>
              ) : null}
              {brand.commentsCount > 1 ? (
                <span className="cell-comment__count">{brand.commentsCount}</span>
              ) : null}
              {when}
            </span>
          </>
        )}
      </button>

      {anchor ? (
        <CommentThread
          brandId={brand.id}
          brandName={brand.name}
          anchor={anchor}
          onClose={() => setAnchor(null)}
          onOpenCard={() => { setAnchor(null); actions.openComments() }}
        />
      ) : null}
    </>
  )
}

/** Окно обсуждения: лента, задачи с галочкой и поле ввода. */
function CommentThread({ brandId, brandName, anchor, onClose, onOpenCard }: {
  brandId: number
  brandName: string
  anchor: HTMLElement
  onClose: () => void
  onOpenCard: () => void
}) {
  const user = useApp((state) => state.bootstrap?.user ?? null)
  const addComment = useApp((state) => state.addComment)
  const completeTask = useApp((state) => state.completeTask)
  const setCommentKind = useApp((state) => state.setCommentKind)
  const toast = useApp((state) => state.toast)

  const [items, setItems] = useState<Comment[] | null>(null)
  const [body, setBody] = useState('')
  const [kind, setKind] = useState<CommentKind>('comment')
  const [due, setDue] = useState('')
  const [busy, setBusy] = useState(false)
  /* Какую задачу закрываем: пока отчёт не написан, галочка не ставится. */
  const [closing, setClosing] = useState<number | null>(null)
  const [report, setReport] = useState('')
  /* На что отвечаем и у какой записи открыто меню действий. */
  const [replyTo, setReplyTo] = useState<Comment | null>(null)
  const [menuFor, setMenuFor] = useState<{ id: number; anchor: HTMLElement } | null>(null)
  const input = useRef<HTMLTextAreaElement | null>(null)

  const reload = async (): Promise<void> => {
    const res = await api.comments(brandId)
    setItems(res.comments)
  }

  useEffect(() => {
    let alive = true
    void api.comments(brandId)
      .then((res) => { if (alive) setItems(res.comments) })
      .catch(() => { if (alive) setItems([]) })
    return () => { alive = false }
  }, [brandId])

  /* Поле ввода растёт под текст — до четырёх строк, дальше прокрутка. */
  useEffect(() => {
    const el = input.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 96)}px`
  }, [body])

  const canWrite = Boolean(user?.permissions.includes('comments.create'))

  async function send(): Promise<void> {
    const text = body.trim()
    if (text === '' || busy) return
    setBusy(true)
    try {
      await addComment(brandId, text, null, kind, kind === 'task' && due !== '' ? due : null, replyTo?.id ?? null)
      setBody('')
      setDue('')
      setKind('comment')
      setReplyTo(null)
      await reload()
      input.current?.focus()
    } catch (err) {
      toast('error', err instanceof ApiRequestError ? err.message : t.comment.failed)
    } finally {
      setBusy(false)
    }
  }

  async function convert(item: Comment): Promise<void> {
    try {
      await setCommentKind(item.id, item.kind === 'task' ? 'comment' : 'task')
      await reload()
    } catch (err) {
      toast('error', err instanceof ApiRequestError ? err.message : t.comment.failed)
    }
  }

  async function finishTask(taskId: number): Promise<void> {
    const text = report.trim()
    if (text === '' || busy) return
    setBusy(true)
    try {
      await completeTask(taskId, text)
      setReport('')
      setClosing(null)
      await reload()
    } catch (err) {
      toast('error', err instanceof ApiRequestError ? err.message : t.comment.failed)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Popover open anchor={anchor} onClose={onClose} role="dialog" minWidth={380} maxWidth={440} focusOnOpen>
      <div className="cell-thread">
        <div className="cell-thread__head">
          <span className="cell-thread__title t-col">{brandName}</span>
          <button type="button" className="cell-thread__link t-meta" onClick={onOpenCard}>
            {t.comment.openCard}
          </button>
        </div>

        <div className="cell-thread__list">
          {items === null ? (
            <div className="cell-thread__loading"><Spinner size={16} /></div>
          ) : items.length === 0 ? (
            <p className="cell-thread__empty t-meta">{t.comment.none}</p>
          ) : (
            [...items].reverse().slice(0, 8).map((item) => {
              /* Вид и галочка могут ждать публикации в черновике — показываем
                 их так, будто они уже применены, но с пометкой. */
              const kind = item.kindPending ?? item.kind
              const done = item.doneAt !== null || item.donePending
              const openTask = kind === 'task' && !done
              const classes = ['cell-thread__item']
              if (item.isDraft) classes.push('cell-thread__item--draft')
              if (openTask) classes.push('cell-thread__item--task')
              if (kind === 'task' && done) classes.push('cell-thread__item--done')
              return (
                <article key={item.id} className={classes.join(' ')}>
                  <Avatar name={item.authorLabel} size={18} color={item.authorColor ?? undefined} />
                  <div className="cell-thread__body">
                    <div className="cell-thread__meta t-meta">
                      <span>{item.authorLabel}</span>
                      <span>
                        {item.isDraft
                          ? t.comment.now
                          : item.writtenAt
                            ? threadDate(item.writtenAt)
                            : threadDate(item.createdAt)}
                      </span>
                      {kind === 'task' && item.dueOn ? (
                        <span className="cell-thread__due">{t.comment.due(dueLabel(item.dueOn))}</span>
                      ) : null}
                    </div>
                    {item.replyTo ? (
                      <p className="cell-thread__quote">
                        <b>{shortName(item.replyTo.author)}</b>
                        {` · ${item.replyTo.body.slice(0, 70)}`}
                      </p>
                    ) : null}
                    <p className="cell-thread__text">{item.body}</p>
                    {kind === 'task' && done ? (
                      <p className="cell-thread__done t-meta">
                        {item.donePending
                          ? t.comment.donePending
                          : t.comment.doneBy(item.doneByName ?? '—')}
                      </p>
                    ) : null}

                    {/* Действия с записью — за троеточием: ответить и
                        перевести заметку в задачу. */}
                    {canWrite ? (
                      <button
                        type="button"
                        className="cell-thread__more"
                        aria-label={t.comment.more}
                        title={t.comment.more}
                        aria-haspopup="menu"
                        onClick={(event) => setMenuFor({ id: item.id, anchor: event.currentTarget })}
                      >
                        ⋯
                      </button>
                    ) : null}
                    {menuFor?.id === item.id ? (
                      <Menu
                        open
                        anchor={menuFor.anchor}
                        label={t.comment.more}
                        minWidth={190}
                        items={[
                          { id: 'reply', label: t.comment.reply },
                          ...(!done && !item.isDraft
                            ? [{
                              id: 'kind',
                              label: kind === 'task' ? t.comment.toComment : t.comment.toTask,
                            } satisfies MenuItem]
                            : []),
                        ]}
                        onClose={() => setMenuFor(null)}
                        onSelect={(id) => {
                          setMenuFor(null)
                          if (id === 'reply') { setReplyTo(item); input.current?.focus() }
                          if (id === 'kind') void convert(item)
                        }}
                      />
                    ) : null}

                    {openTask && canWrite ? (
                      closing === item.id ? (
                        <div className="cell-thread__close">
                          <textarea
                            className="cell-thread__input"
                            rows={2}
                            autoFocus
                            value={report}
                            placeholder={t.comment.reportPlaceholder}
                            aria-label={t.comment.reportPlaceholder}
                            onChange={(event) => setReport(event.target.value)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' && !event.shiftKey) {
                                event.preventDefault()
                                void finishTask(item.id)
                              }
                              if (event.key === 'Escape') { setClosing(null); setReport('') }
                            }}
                          />
                          <div className="cell-thread__close-actions">
                            <button
                              type="button"
                              className="cell-thread__done-btn"
                              disabled={report.trim() === '' || busy}
                              onClick={() => void finishTask(item.id)}
                            >
                              {t.comment.closeTask}
                            </button>
                            <button
                              type="button"
                              className="cell-thread__cancel t-meta"
                              onClick={() => { setClosing(null); setReport('') }}
                            >
                              {t.comment.cancel}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <Checkbox
                          checked={false}
                          label={<span className="t-meta">{t.comment.markDone}</span>}
                          onChange={() => { setClosing(item.id); setReport('') }}
                        />
                      )
                    ) : null}
                  </div>
                </article>
              )
            })
          )}
        </div>

        {canWrite ? (
          <div className="cell-thread__form">
            <div className="cell-thread__kinds" role="tablist" aria-label={t.comment.kindLabel}>
              <button
                type="button"
                role="tab"
                aria-selected={kind === 'comment'}
                className={kind === 'comment' ? 'cell-thread__kind cell-thread__kind--on' : 'cell-thread__kind'}
                onClick={() => setKind('comment')}
              >
                {t.comment.kindComment}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={kind === 'task'}
                className={kind === 'task' ? 'cell-thread__kind cell-thread__kind--on cell-thread__kind--task' : 'cell-thread__kind'}
                onClick={() => setKind('task')}
              >
                {t.comment.kindTask}
              </button>
              {kind === 'task' ? (
                <label className="cell-thread__due-field">
                  <span className="visually-hidden">{t.comment.dueLabel}</span>
                  <input
                    type="date"
                    className="cell-thread__date"
                    value={due}
                    onChange={(event) => setDue(event.target.value)}
                  />
                </label>
              ) : null}
            </div>

            {replyTo ? (
              <div className="cell-thread__reply">
                <span className="cell-thread__reply-text">
                  <b>{shortName(replyTo.authorLabel)}</b>
                  {` · ${replyTo.body.slice(0, 60)}`}
                </span>
                <button
                  type="button"
                  className="cell-thread__reply-cancel"
                  aria-label={t.comment.replyCancel}
                  onClick={() => setReplyTo(null)}
                >
                  <IconClose size={16} />
                </button>
              </div>
            ) : null}

            {/* Поле ввода с самолётиком внутри — как в любом чате. */}
            <div className={kind === 'task' ? 'cell-thread__composer cell-thread__composer--task' : 'cell-thread__composer'}>
              <textarea
                ref={input}
                className="cell-thread__input"
                rows={1}
                value={body}
                placeholder={kind === 'task' ? t.comment.taskPlaceholder : t.comment.placeholder}
                aria-label={kind === 'task' ? t.comment.taskPlaceholder : t.comment.placeholder}
                onChange={(event) => setBody(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault()
                    void send()
                  }
                }}
              />
              <button
                type="button"
                className="cell-thread__send"
                disabled={body.trim() === '' || busy}
                aria-label={t.comment.send}
                title={t.comment.send}
                onClick={() => void send()}
              >
                {busy ? <Spinner size={14} /> : <IconSend size={16} />}
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </Popover>
  )
}
